import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import Layout from './components/Layout';
import OnboardingWizard from './components/OnboardingWizard';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Methods from './pages/Methods';
import MethodWorksheet from './pages/MethodWorksheet';
import CustomMethodWizard from './pages/CustomMethodWizard';
import MethodShop from './pages/MethodShop';
import Invoices from './pages/Invoices';
import Bookings from './pages/Bookings';
import CalendarPage from './pages/Calendar';
import PortalDashboard, { PortalLogin, PortalSetup } from './pages/Portal';
import PublicFormPage from './pages/PublicFormPage';
import Settings from './pages/Settings';
import Reports from './pages/Reports';
import BookingPage from './pages/BookingPage';
import LegalPage from './pages/LegalPage';
import Integrations from './pages/Integrations';
import MeinKonto from './pages/MeinKonto';
import AdminPage from './pages/Admin';
import QualityManagement from './pages/QualityManagement';
import SitzungAnlegen from './pages/SitzungAnlegen';
import SupportPage from './pages/SupportPage';
import Nachrichten from './pages/Nachrichten';
import HelpCenter from './pages/HelpCenter';
import HomePage from './pages/homepage/HomePage';
import StatusPage from './pages/StatusPage';
import KontaktPage from './pages/KontaktPage';
import ChangelogPage from './pages/ChangelogPage';
import PublicHelpPage from './pages/PublicHelpPage';
import InvitationAccept from './pages/InvitationAccept';
import NotFound from './pages/NotFound';
import ErrorBoundary from './components/ErrorBoundary';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, refreshUser } = useAuth();
  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', color: 'var(--text-muted)', fontSize: '0.9rem',
      }}>
        Wird geladen...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  // Show onboarding wizard if not completed
  if (user.onboarding_completed === false) {
    return (
      <OnboardingWizard
        user={user}
        onComplete={() => refreshUser()}
      />
    );
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'superadmin') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/dashboard" replace /> : <>{children}</>;
}

/** Show HelpCenter inside Layout when logged in, otherwise PublicHelpPage */
function SmartHelpRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? (
    <ProtectedRoute>
      <Layout />
    </ProtectedRoute>
  ) : (
    <PublicHelpPage />
  );
}

function LanguageSync() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const lang = pathname.startsWith('/en') ? 'en' : 'de';
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    document.documentElement.lang = lang;
  }, [pathname, i18n]);

  return null;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Homepage — oeffentlich, eingeloggte User zum Dashboard */}
      <Route path="/" element={<PublicRoute><HomePage /></PublicRoute>} />
      <Route path="/en" element={<PublicRoute><HomePage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal/setup/:token" element={<PortalSetup />} />
      <Route path="/portal" element={<PortalDashboard />} />
      <Route path="/formular/:token" element={<PublicFormPage />} />
      <Route path="/buchen/:slug" element={<BookingPage />} />
      <Route path="/impressum" element={<LegalPage type="impressum" />} />
      <Route path="/datenschutz" element={<LegalPage type="datenschutz" />} />
      <Route path="/agb" element={<LegalPage type="agb" />} />
      <Route path="/avv" element={<LegalPage type="avv" />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/kontakt" element={<KontaktPage />} />
      <Route path="/changelog" element={<ChangelogPage />} />
      <Route path="/einladung/:token" element={<InvitationAccept />} />
      <Route path="/hilfe" element={<SmartHelpRoute />}>
        <Route index element={<HelpCenter />} />
        <Route path=":slug" element={<HelpCenter />} />
      </Route>
      {/* Methoden-Arbeitsblatt — eigene Seite ohne Navigation, druckoptimiert */}
      <Route path="/methoden/:id/arbeitsblatt" element={<ProtectedRoute><MethodWorksheet /></ProtectedRoute>} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/klienten" element={<Clients />} />
        <Route path="/klienten/:id" element={<ClientDetail />} />
        <Route path="/sitzung-anlegen" element={<SitzungAnlegen />} />
        <Route path="/nachrichten" element={<Nachrichten />} />
        <Route path="/methoden" element={<Methods />} />
        <Route path="/methoden/erstellen/wizard" element={<CustomMethodWizard />} />
        <Route path="/methoden/shop" element={<MethodShop />} />
        <Route path="/rechnungen" element={<Invoices />} />
        <Route path="/buchungen" element={<Bookings />} />
        <Route path="/kalender" element={<CalendarPage />} />
        <Route path="/berichte" element={<Reports />} />
        <Route path="/qualitaet" element={<QualityManagement />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/integrationen" element={<Integrations />} />
        <Route path="/einstellungen" element={<Settings />} />
        <Route path="/mein-konto" element={<MeinKonto />} />
        <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
      </Route>
      {/* 404 Catch-All */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ToastProvider>
          <AuthProvider>
            <LanguageSync />
            <AppRoutes />
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
